import React from 'react';
import { Github, Linkedin, Mail, Heart, ExternalLink } from 'lucide-react';
import { personalInfo } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'About', href: '#about' },
    { name: 'Projects', href: '#projects' },
    { name: 'Skills', href: '#skills' },
    { name: 'Blog', href: '#blog' }
  ];

  const projectLinks = [
    { name: 'Football Highlights', href: '#projects' },
    { name: 'Fashion GAN', href: '#projects' },
    { name: 'Crop Disease AI', href: '#projects' },
    { name: 'Celebrity Recognition', href: '#projects' }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="glass-card border-t border-[rgba(255,255,255,0.1)] pt-20 pb-10 mt-32 relative overflow-hidden">
      {/* Glassmorphism background elements */}
      <div className="absolute top-10 right-20 w-32 h-32 rounded-full bg-gradient-to-br from-[rgba(218,255,1,0.08)] to-transparent backdrop-blur-sm opacity-30"></div>
      <div className="absolute bottom-10 left-20 w-24 h-24 rounded-full bg-gradient-to-tl from-[rgba(218,255,1,0.06)] to-transparent backdrop-blur-sm opacity-20"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Brand Section with glassmorphism */}
            <div className="lg:col-span-2">
              <div className="glass-light rounded-2xl p-6 mb-6">
                <h3 className="text-2xl font-bold text-white mb-3 relative">
                  <span className="relative z-10">
                    Arjun <span className="text-[rgb(218,255,1)] relative">
                      Bhusal
                      <div className="absolute inset-0 bg-[rgb(218,255,1)] opacity-20 blur-lg rounded-lg"></div>
                    </span>
                  </span>
                </h3>
                <p className="text-[rgb(161,161,170)] leading-relaxed max-w-md">
                  AI/ML Engineer passionate about building intelligent systems that solve real-world problems. 
                  Specializing in computer vision, deep learning, and full-stack development.
                </p>
              </div>
              
              {/* Contact Info with glass effect */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-3 glass-light rounded-xl p-3">
                  <Mail size={16} className="text-[rgb(218,255,1)]" />
                  <a 
                    href={`mailto:${personalInfo.email}`}
                    className="text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] transition-colors duration-300"
                  >
                    {personalInfo.email}
                  </a>
                </div>
                <div className="flex items-center space-x-3 glass-light rounded-xl p-3">
                  <span className="text-[rgb(218,255,1)]">📍</span>
                  <span className="text-[rgb(161,161,170)]">{personalInfo.location}</span>
                </div>
              </div>

              {/* Social Links with enhanced glass effect */}
              <div className="flex space-x-4">
                <a
                  href={personalInfo.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glass-light w-12 h-12 rounded-2xl flex items-center justify-center text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] hover:border-[rgba(218,255,1,0.3)] hover:transform hover:translate-y-[-2px] hover:shadow-[0_0_20px_rgba(218,255,1,0.3)] transition-all duration-300"
                >
                  <Github size={20} />
                </a>
                <a
                  href={personalInfo.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glass-light w-12 h-12 rounded-2xl flex items-center justify-center text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] hover:border-[rgba(218,255,1,0.3)] hover:transform hover:translate-y-[-2px] hover:shadow-[0_0_20px_rgba(218,255,1,0.3)] transition-all duration-300"
                >
                  <Linkedin size={20} />
                </a>
                <a
                  href={`mailto:${personalInfo.email}`}
                  className="glass-light w-12 h-12 rounded-2xl flex items-center justify-center text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] hover:border-[rgba(218,255,1,0.3)] hover:transform hover:translate-y-[-2px] hover:shadow-[0_0_20px_rgba(218,255,1,0.3)] transition-all duration-300"
                >
                  <Mail size={20} />
                </a>
              </div>
            </div>

            {/* Quick Links with glass background */}
            <div className="glass-light rounded-2xl p-6">
              <h4 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <div className="w-2 h-6 bg-[rgb(218,255,1)] rounded-full shadow-[0_0_8px_rgba(218,255,1,0.5)]"></div>
                Quick Links
              </h4>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] transition-colors duration-300 text-left hover:translate-x-1 transform transition-transform"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Featured Projects with glass background */}
            <div className="glass-light rounded-2xl p-6">
              <h4 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <div className="w-2 h-6 bg-[rgb(218,255,1)] rounded-full shadow-[0_0_8px_rgba(218,255,1,0.5)]"></div>
                Featured Work
              </h4>
              <ul className="space-y-3">
                {projectLinks.map((link, index) => (
                  <li key={index}>
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] transition-all duration-300 text-left flex items-center group hover:translate-x-1 transform transition-transform"
                    >
                      <span>{link.name}</span>
                      <ExternalLink size={12} className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Divider with glass effect */}
          <div className="glass-light rounded-full h-px mb-8"></div>

          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Copyright with glass background */}
            <div className="glass-light rounded-full px-6 py-3">
              <div className="flex items-center space-x-2 text-[rgb(161,161,170)]">
                <span>© {currentYear} Arjun Bhusal. Made with</span>
                <Heart size={16} className="text-red-500 fill-current animate-pulse" />
                <span>and lots of ☕</span>
              </div>
            </div>

            {/* Back to Top with glass effect */}
            <button
              onClick={scrollToTop}
              className="glass-light rounded-full px-6 py-3 flex items-center space-x-2 text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] hover:border-[rgba(218,255,1,0.3)] transition-all duration-300 hover:transform hover:translate-y-[-2px] hover:shadow-[0_0_15px_rgba(218,255,1,0.2)] group"
            >
              <span>Back to Top</span>
              <div className="w-6 h-6 rounded border border-current group-hover:border-[rgb(218,255,1)] flex items-center justify-center group-hover:transform group-hover:translate-y-[-1px] transition-all duration-300">
                <span className="text-xs">↑</span>
              </div>
            </button>
          </div>

          {/* Bottom Note with glass effect */}
          <div className="mt-8 pt-6">
            <div className="glass-light rounded-2xl p-4 text-center">
              <p className="text-sm text-[rgb(161,161,170)]">
                Built with React, FastAPI, and MongoDB • Enhanced with Glassmorphism • 
                <button 
                  onClick={() => scrollToSection('#contact')}
                  className="ml-1 text-[rgb(218,255,1)] hover:underline transition-colors duration-300"
                >
                  Let's connect!
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;